import type {
  Alerta,
  EstadoDiarioRespuesta,
  FiltroAlertas,
  FlotaEstadoRespuesta,
  JornadasRespuesta,
  LoginBody,
  LoginRespuesta,
  MicrotrackEstado,
  CatalogosRespuesta,
  MensajeStream,
  NombresRuteoRespuesta,
  RecorridoRespuesta,
  TipoEventoStream,
  ViajeDetalle,
  ViajesRespuesta,
  ErrorApi,
  EventosStream,
  TicketStreamRespuesta,
  CalcularRutaBody,
  CalculoRuta,
  FiltroTroncales,
  ReglaAlerta,
  ReglaAlertaActualizacionBody,
  ReglaAlertaBody,
  Ruta,
  RutaBody,
  RutaGuardada,
  TroncalDetalle,
  TroncalesRespuesta,
} from '@tms/contratos';
import { ErrorApiTms, MENSAJE_MOTOR_CAIDO } from './types';
import type { AlertasRespuesta, ApiTms, EstadoConexion, FiltrosFlota, FiltrosReglas, FiltrosViajes } from './types';

type Query = Record<string, string | number | boolean | string[] | undefined | null>;

const TIPOS_STREAM: TipoEventoStream[] = ['posicion', 'viaje', 'alerta', 'ping'];
const BACKOFF_INICIAL_MS = 1000;
const BACKOFF_MAXIMO_MS = 30000;

function construirQuery(q?: Query): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    // Los arreglos se envían separados por coma. TODO(decision): confirmar formato con la API.
    p.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Implementación contra la API del TMS (VITE_API_URL). */
export class ApiReal implements ApiTms {
  readonly esMock = false;

  constructor(
    private readonly baseUrl: string,
    private readonly obtenerToken: () => string | null,
    private readonly alNoAutorizado: () => void,
  ) {}

  private async pedir<T>(metodo: 'GET' | 'POST' | 'PUT' | 'DELETE', ruta: string, opts: { query?: Query; body?: unknown; sinAuth?: boolean } = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = this.obtenerToken();
    if (token && !opts.sinAuth) headers.Authorization = `Bearer ${token}`;
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

    let resp: Response;
    try {
      resp = await fetch(`${this.baseUrl}${ruta}${construirQuery(opts.query)}`, {
        method: metodo,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      });
    } catch (e) {
      throw new ErrorApiTms(0, `No se pudo conectar con el servidor (${e instanceof Error ? e.message : 'error de red'}).`);
    }

    if (resp.status === 401 && !opts.sinAuth) {
      this.alNoAutorizado();
      throw new ErrorApiTms(401, 'Tu sesión venció. Volvé a iniciar sesión.');
    }
    if (!resp.ok) {
      let mensaje = resp.status === 403 ? 'Tu rol no tiene permiso para esta acción.' : `Error ${resp.status} del servidor.`;
      try {
        const cuerpo = (await resp.json()) as Partial<ErrorApi>;
        if (cuerpo.message) mensaje = Array.isArray(cuerpo.message) ? cuerpo.message.join(' · ') : cuerpo.message;
      } catch {
        // El cuerpo no era JSON: se deja el mensaje genérico con el código de estado.
      }
      throw new ErrorApiTms(resp.status, mensaje);
    }
    // 204 o cuerpo vacío (p. ej. DELETE de una regla).
    const texto = await resp.text();
    return (texto ? JSON.parse(texto) : undefined) as T;
  }

  async login(body: LoginBody): Promise<LoginRespuesta> {
    try {
      return await this.pedir<LoginRespuesta>('POST', '/auth/login', { body, sinAuth: true });
    } catch (e) {
      if (e instanceof ErrorApiTms && e.status === 401) throw new ErrorApiTms(401, 'Email o contraseña incorrectos.');
      throw e;
    }
  }

  flotaEstado(filtros?: FiltrosFlota): Promise<FlotaEstadoRespuesta> {
    return this.pedir('GET', '/flota/estado', { query: { ...filtros } });
  }

  viajes(filtros?: FiltrosViajes): Promise<ViajesRespuesta> {
    return this.pedir('GET', '/viajes', { query: { ...filtros } });
  }

  viaje(id: number): Promise<ViajeDetalle> {
    return this.pedir('GET', `/viajes/${id}`);
  }

  recorrido(id: number, muestreoSeg?: number): Promise<RecorridoRespuesta> {
    return this.pedir('GET', `/viajes/${id}/recorrido`, { query: { muestreoSeg } });
  }

  alertas(filtros?: FiltroAlertas): Promise<AlertasRespuesta> {
    return this.pedir('GET', '/alertas', { query: { ...filtros } });
  }

  resolverAlerta(id: number, motivo?: string): Promise<Alerta> {
    return this.pedir('POST', `/alertas/${id}/resolucion`, { body: motivo ? { motivo } : {} });
  }

  justificarAlerta(id: number, motivo: string): Promise<Alerta> {
    return this.pedir('POST', `/alertas/${id}/justificacion`, { body: { motivo } });
  }

  jornadas(fecha: string): Promise<JornadasRespuesta> {
    return this.pedir('GET', '/jornadas', { query: { fecha } });
  }

  estadoDiario(fecha: string): Promise<EstadoDiarioRespuesta> {
    return this.pedir('GET', '/unidades/estado-diario', { query: { fecha } });
  }

  estadoDiarioRango(desde: string, hasta: string): Promise<EstadoDiarioRespuesta> {
    return this.pedir('GET', '/unidades/estado-diario', { query: { desde, hasta } });
  }

  cambiarRutaViaje(viajeId: number, rutaId: number): Promise<ViajeDetalle> {
    return this.pedir('PUT', `/viajes/${viajeId}/ruta`, { body: { rutaId } });
  }

  // ---------- Ruteo ----------

  troncales(filtro?: FiltroTroncales): Promise<TroncalesRespuesta> {
    return this.pedir('GET', '/troncales', { query: { ...filtro } });
  }

  troncal(id: number): Promise<TroncalDetalle> {
    return this.pedir('GET', `/troncales/${id}`);
  }

  /** Crear, editar y calcular pasan por el motor de ruteo: un 503 se traduce a un mensaje claro. */
  private async conMotor<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof ErrorApiTms && e.status === 503) throw new ErrorApiTms(503, MENSAJE_MOTOR_CAIDO);
      throw e;
    }
  }

  crearRuta(troncalId: number, body: RutaBody): Promise<RutaGuardada> {
    return this.conMotor(() => this.pedir('POST', `/troncales/${troncalId}/rutas`, { body }));
  }

  actualizarRuta(id: number, body: RutaBody): Promise<RutaGuardada> {
    return this.conMotor(() => this.pedir('PUT', `/rutas/${id}`, { body }));
  }

  bajaRuta(id: number): Promise<Ruta> {
    return this.pedir('DELETE', `/rutas/${id}`);
  }

  calcularRuta(body: CalcularRutaBody): Promise<CalculoRuta> {
    return this.conMotor(() => this.pedir('POST', '/rutas/calcular', { body }));
  }

  nombresRuteo(rutas: number[], paradas: number[]): Promise<NombresRuteoRespuesta> {
    return this.pedir('GET', '/ruteo/nombres', { query: { rutas: rutas.length ? rutas.join(',') : undefined, paradas: paradas.length ? paradas.join(',') : undefined } });
  }

  // ---------- Reglas de alerta ----------

  reglasAlerta(filtros?: FiltrosReglas): Promise<ReglaAlerta[]> {
    return this.pedir('GET', '/configuracion/reglas-alerta', { query: { ...filtros } });
  }

  crearReglaAlerta(body: ReglaAlertaBody): Promise<ReglaAlerta> {
    return this.pedir('POST', '/configuracion/reglas-alerta', { body });
  }

  actualizarReglaAlerta(id: number, body: ReglaAlertaActualizacionBody): Promise<ReglaAlerta> {
    return this.pedir('PUT', `/configuracion/reglas-alerta/${id}`, { body });
  }

  async borrarReglaAlerta(id: number): Promise<void> {
    await this.pedir<unknown>('DELETE', `/configuracion/reglas-alerta/${id}`);
  }

  catalogos(): Promise<CatalogosRespuesta> {
    return this.pedir('GET', '/catalogos');
  }

  microtrackEstado(): Promise<MicrotrackEstado> {
    return this.pedir('GET', '/microtrack/estado');
  }

  /**
   * SSE con reconexión automática: backoff exponencial de 1 s a 30 s.
   * Autenticación por ticket de un solo uso (POST /stream/ticket con el JWT en el header):
   * en cada (re)conexión se pide un ticket nuevo, así el JWT nunca viaja en la URL.
   */
  suscribir(onMensaje: (m: MensajeStream) => void, onEstado?: (e: EstadoConexion) => void): () => void {
    let fuente: EventSource | null = null;
    let cerrado = false;
    let espera = BACKOFF_INICIAL_MS;
    let temporizador: ReturnType<typeof setTimeout> | undefined;

    const reprogramar = () => {
      if (cerrado) return;
      onEstado?.('reconectando');
      temporizador = setTimeout(() => void conectar(), espera);
      espera = Math.min(espera * 2, BACKOFF_MAXIMO_MS);
    };

    const conectar = async () => {
      if (cerrado || !this.obtenerToken()) return;
      let ticket: string;
      try {
        ({ ticket } = await this.pedir<TicketStreamRespuesta>('POST', '/stream/ticket'));
      } catch (e) {
        console.warn('No se pudo obtener el ticket del stream; se reintenta', e);
        reprogramar();
        return;
      }
      if (cerrado) return;

      const es = new EventSource(`${this.baseUrl}/stream${construirQuery({ ticket })}`);
      fuente = es;

      es.onopen = () => {
        espera = BACKOFF_INICIAL_MS;
        onEstado?.('conectado');
      };

      for (const tipo of TIPOS_STREAM) {
        es.addEventListener(tipo, (ev) => {
          try {
            const datos = JSON.parse((ev as MessageEvent<string>).data) as EventosStream[typeof tipo];
            onMensaje({ tipo, datos } as MensajeStream);
          } catch (e) {
            console.warn(`Mensaje "${tipo}" del stream con formato inválido`, e);
          }
        });
      }

      es.onerror = () => {
        // El ticket es de un solo uso: el reintento nativo de EventSource fallaría.
        // Se cierra y se reprograma con espera creciente y ticket nuevo.
        es.close();
        if (fuente === es) fuente = null;
        reprogramar();
      };
    };

    void conectar();

    return () => {
      cerrado = true;
      if (temporizador) clearTimeout(temporizador);
      fuente?.close();
      fuente = null;
    };
  }
}
